/**
 * Financial rounding calculations for municipal bond processing
 * All functions are pure (Level 5) with no side effects
 * @module calculators/core/financial-math/rounding
 */

import Decimal from 'decimal.js-light';
import { PRINCIPAL_AMOUNTS } from '~/config/financial-constants';

/**
 * Rounds a principal amount DOWN to the nearest $5,000 increment
 * Used for standard distribution calculations where we want to ensure
 * we don't exceed available capacity
 * @param amount - Raw principal amount to round
 * @returns Principal rounded down to nearest $5,000
 * @pure Level 5 calculation - no side effects
 */
export function roundPrincipalDown(amount: number): number {
  return (
    Math.floor(amount / PRINCIPAL_AMOUNTS.PRINCIPAL_INCREMENT) *
    PRINCIPAL_AMOUNTS.PRINCIPAL_INCREMENT
  );
}

/**
 * Rounds a principal amount UP to the nearest $5,000 increment
 * Used for constraint satisfaction and minimum requirements where
 * we need to ensure we meet minimum thresholds
 * @param amount - Raw principal amount to round
 * @returns Principal rounded up to nearest $5,000
 * @pure Level 5 calculation - no side effects
 */
export function roundPrincipalUp(amount: number): number {
  return (
    Math.ceil(amount / PRINCIPAL_AMOUNTS.PRINCIPAL_INCREMENT) *
    PRINCIPAL_AMOUNTS.PRINCIPAL_INCREMENT
  );
}

/**
 * Rounds a principal amount to the NEAREST $5,000 increment
 * Used for adjustments and first-period calculations where we want
 * the closest valid principal amount
 * @param amount - Raw principal amount to round
 * @returns Principal rounded to nearest $5,000
 * @pure Level 5 calculation - no side effects
 */
export function roundPrincipalNearest(amount: number): number {
  return (
    Math.round(amount / PRINCIPAL_AMOUNTS.PRINCIPAL_INCREMENT) *
    PRINCIPAL_AMOUNTS.PRINCIPAL_INCREMENT
  );
}

/**
 * Rounds an interest amount to the nearest dollar
 * Used for display and final interest calculations
 * @param amount - Raw interest amount to round
 * @returns Interest rounded to nearest dollar
 * @pure Level 5 calculation - no side effects
 */
export function roundInterestToNearestDollar(amount: number): number {
  return Math.round(amount);
}

/**
 * Rounds an interest amount to the nearest cent (2 decimal places)
 * Uses decimal.js-light for precise financial rounding
 *
 * Note: When calculations are done entirely in Decimal (recommended),
 * this function only needs single-step rounding. The two-step approach
 * was needed when fixing floating-point errors from JS math operations.
 *
 * @param amount - Raw interest amount to round
 * @returns Interest rounded to nearest cent
 * @pure Level 5 calculation - no side effects
 *
 * @example
 * roundInterestToNearestCent(7884.375) // Returns 7884.38
 * roundInterestToNearestCent(7884.374) // Returns 7884.37
 */
export function roundInterestToNearestCent(amount: number): number {
  return new Decimal(amount).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
}

/**
 * Rounds a millage rate to specified decimal precision
 * Used for millage rate calculations and display
 * @param rate - Raw millage rate to round
 * @param precision - Number of decimal places (default 4)
 * @returns Millage rate rounded to specified precision
 * @pure Level 5 calculation - no side effects
 */
export function roundMillageRate(rate: number, precision: number = 4): number {
  const multiplier = 10 ** precision;
  return Math.round(rate * multiplier) / multiplier;
}
